import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserRole } from '../../../context/UserRoleProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Loader2, Edit, Save, X, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, Filter, Users, Mail } from 'lucide-react'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export default function AdminUsers() {
  const navigate = useNavigate()
  const { isSuperAdmin, loading: roleLoading } = useUserRole()
  const [users, setUsers] = useState([])
  const [allParishes, setAllParishes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingUserId, setEditingUserId] = useState(null)
  const [editingRoles, setEditingRoles] = useState([])
  const [saving, setSaving] = useState(false)
  const [sendingTestEmailTo, setSendingTestEmailTo] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [filterParish, setFilterParish] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const availableRoles = ['user', 'parish-admin', 'super-admin']

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setCurrentPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filterParish, filterRole, pageSize])

  useEffect(() => {
    if (roleLoading) return
    if (!isSuperAdmin) {
      setError('You do not have permission to access this page.')
      setLoading(false)
      return
    }
    fetchUsers()
    fetchAllParishes()
  }, [isSuperAdmin, roleLoading])

  const fetchAllParishes = async () => {
    try {
      let all = []
      let page = 1
      let hasMore = true
      while (hasMore) {
        const response = await api.searchParishes({ page, limit: 100 })
        const parishes = response.data || []
        all = [...all, ...parishes]
        hasMore = response.meta?.hasNextPage || false
        page++
        if (page > 100) break
      }
      setAllParishes(all)
    } catch (err) {
      console.error('Failed to fetch parishes:', err)
    }
  }

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const usersData = await api.getAllUsers()
      setUsers(usersData || [])
      setLoading(false)
    } catch (err) {
      console.error('Failed to fetch users:', err)
      setError(err.message || 'Failed to load users')
      setLoading(false)
    }
  }

  const handleEdit = (user) => {
    setEditingUserId(user.id)
    fetchUserRoles(user.id)
  }

  const fetchUserRoles = async (userId) => {
    try {
      const rolesData = await api.getUserRoles(userId)
      const roles = (rolesData.roles || []).map(role => ({
        roleName: role.name,
        parishId: role.parishId || null
      }))
      setEditingRoles(roles)
    } catch (err) {
      console.error('Failed to fetch user roles:', err)
      setError(err.message || 'Failed to load user roles')
    }
  }

  const handleCancelEdit = () => {
    setEditingUserId(null)
    setEditingRoles([])
  }

  const handleAddRole = () => {
    setEditingRoles([...editingRoles, { roleName: 'user', parishId: null }])
  }

  const handleRemoveRole = (index) => {
    setEditingRoles(editingRoles.filter((_, i) => i !== index))
  }

  const handleRoleChange = (index, roleName) => {
    const updatedRoles = [...editingRoles]
    updatedRoles[index] = {
      roleName,
      parishId: roleName === 'parish-admin' ? (updatedRoles[index]?.parishId || null) : null
    }
    setEditingRoles(updatedRoles)
  }

  const handleParishChange = (index, parishId) => {
    const updatedRoles = [...editingRoles]
    updatedRoles[index] = {
      ...updatedRoles[index],
      parishId: parishId || null
    }
    setEditingRoles(updatedRoles)
  }

  const handleSaveRoles = async (userId) => {
    try {
      setSaving(true)
      setError(null)
      const invalidRoles = editingRoles.filter(r => r.roleName === 'parish-admin' && !r.parishId)
      if (invalidRoles.length > 0) {
        setError('Parish-admin roles must have an associated parish selected.')
        setSaving(false)
        return
      }
      const rolesToUpdate = editingRoles.map(r => ({
        roleName: r.roleName,
        parishId: r.parishId || null
      }))
      await api.updateUserRoles(userId, rolesToUpdate)
      await fetchUsers()
      setEditingUserId(null)
      setEditingRoles([])
    } catch (err) {
      console.error('Failed to update user roles:', err)
      setError(err.message || 'Failed to update user roles')
    } finally {
      setSaving(false)
    }
  }

  const handleSendTestEmail = async (userId) => {
    try {
      setSendingTestEmailTo(userId)
      const result = await api.sendTestNotificationEmailToUser(userId)
      alert(result.message || 'Test email sent!')
    } catch (err) {
      alert(err.message || 'Failed to send test email')
    } finally {
      setSendingTestEmailTo(null)
    }
  }

  // Extract unique parishes and roles for filter dropdowns
  const { uniqueParishes, uniqueRoles } = useMemo(() => {
    const parishesSet = new Set()
    const rolesSet = new Set()
    users.forEach(user => {
      if (user.parishes && Array.isArray(user.parishes)) {
        user.parishes.forEach(parish => {
          if (parish.parishId && parish.name) {
            parishesSet.add(JSON.stringify({ id: parish.parishId, name: parish.name }))
          }
        })
      }
      if (user.roles && Array.isArray(user.roles)) {
        user.roles.forEach(role => {
          const roleName = typeof role === 'string' ? role : role.name
          if (roleName) rolesSet.add(roleName)
        })
      }
    })
    const parishes = Array.from(parishesSet).map(p => JSON.parse(p))
    parishes.sort((a, b) => a.name.localeCompare(b.name))
    return { uniqueParishes: parishes, uniqueRoles: Array.from(rolesSet).sort() }
  }, [users])

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let filtered = users.filter(user => {
      const matchesSearch = !debouncedSearch ||
        user.email?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        user.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        user.firstName?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(debouncedSearch.toLowerCase())

      const matchesParish = !filterParish ||
        (user.parishes && user.parishes.some(p => p.parishId === filterParish))

      const matchesRole = !filterRole ||
        (user.roles && user.roles.some(r => {
          const roleName = typeof r === 'string' ? r : r.name
          return roleName === filterRole
        }))

      return matchesSearch && matchesParish && matchesRole
    })

    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue
        if (sortField === 'parish') {
          aValue = (a.parishes?.length > 0) ? a.parishes[0].name?.toLowerCase() || '' : ''
          bValue = (b.parishes?.length > 0) ? b.parishes[0].name?.toLowerCase() || '' : ''
        } else if (sortField === 'role') {
          const aRoles = Array.isArray(a.roles) ? a.roles : []
          const bRoles = Array.isArray(b.roles) ? b.roles : []
          aValue = aRoles.length > 0 ? (typeof aRoles[0] === 'string' ? aRoles[0] : aRoles[0].name || '').toLowerCase() : ''
          bValue = bRoles.length > 0 ? (typeof bRoles[0] === 'string' ? bRoles[0] : bRoles[0].name || '').toLowerCase() : ''
        } else if (sortField === 'email') {
          aValue = a.email?.toLowerCase() || ''
          bValue = b.email?.toLowerCase() || ''
        } else if (sortField === 'name') {
          aValue = a.name?.toLowerCase() || a.firstName?.toLowerCase() || ''
          bValue = b.name?.toLowerCase() || b.firstName?.toLowerCase() || ''
        }
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
        return 0
      })
    }

    return filtered
  }, [users, debouncedSearch, filterParish, filterRole, sortField, sortDirection])

  // Pagination calculations
  const totalItems = filteredUsers.length
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const startIndex = (safePage - 1) * pageSize
  const endIndex = Math.min(startIndex + pageSize, totalItems)
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex)

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3.5 h-3.5 ml-1 text-charcoal-subtle" />
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-gold" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 text-gold" />
  }

  const activeFilterCount = [filterParish, filterRole, debouncedSearch].filter(Boolean).length

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (!isSuperAdmin) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-6">
        <p className="text-red-600">You do not have permission to access this page.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold font-display text-charcoal mb-1">Manage Users</h1>
          <p className="text-charcoal-muted text-sm">
            {totalItems} user{totalItems !== 1 ? 's' : ''}{activeFilterCount > 0 ? ' matching filters' : ' total'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white border border-surface-border rounded-lg p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Search */}
          <div className="flex-1 min-w-[250px]">
            <Label htmlFor="search" className="mb-1.5 block text-xs font-medium text-charcoal-muted uppercase tracking-wide">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-subtle" />
              <Input
                id="search"
                type="text"
                placeholder="Search by email, name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Parish Filter */}
          <div className="min-w-[200px]">
            <Label htmlFor="filter-parish" className="mb-1.5 block text-xs font-medium text-charcoal-muted uppercase tracking-wide">Parish</Label>
            <select
              id="filter-parish"
              value={filterParish}
              onChange={(e) => setFilterParish(e.target.value)}
              className="flex h-10 w-full rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-white"
            >
              <option value="">All Parishes</option>
              {uniqueParishes.map(parish => (
                <option key={parish.id} value={parish.id}>{parish.name}</option>
              ))}
            </select>
          </div>

          {/* Role Filter */}
          <div className="min-w-[180px]">
            <Label htmlFor="filter-role" className="mb-1.5 block text-xs font-medium text-charcoal-muted uppercase tracking-wide">Role</Label>
            <select
              id="filter-role"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="flex h-10 w-full rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-white"
            >
              <option value="">All Roles</option>
              {uniqueRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilterParish('')
                setFilterRole('')
                setSearchQuery('')
              }}
              className="text-charcoal-muted hover:text-charcoal text-xs"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Clear {activeFilterCount > 1 ? `(${activeFilterCount})` : ''}
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-surface-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-linen/50 border-b border-surface-border">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-charcoal-muted uppercase tracking-wider cursor-pointer hover:text-charcoal transition-colors select-none"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center">
                    Email
                    <SortIcon field="email" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-charcoal-muted uppercase tracking-wider cursor-pointer hover:text-charcoal transition-colors select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center">
                    Name
                    <SortIcon field="name" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-charcoal-muted uppercase tracking-wider cursor-pointer hover:text-charcoal transition-colors select-none"
                  onClick={() => handleSort('role')}
                >
                  <div className="flex items-center">
                    Roles
                    <SortIcon field="role" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-charcoal-muted uppercase tracking-wider cursor-pointer hover:text-charcoal transition-colors select-none"
                  onClick={() => handleSort('parish')}
                >
                  <div className="flex items-center">
                    Parishes
                    <SortIcon field="parish" />
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-charcoal-muted uppercase tracking-wider w-[100px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-12 text-center">
                    <Users className="w-8 h-8 text-charcoal-subtle mx-auto mb-2" />
                    <p className="text-charcoal-muted text-sm">
                      {activeFilterCount > 0 ? 'No users match your filters.' : 'No users found.'}
                    </p>
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setFilterParish(''); setFilterRole(''); setSearchQuery('') }}
                        className="mt-2 text-gold hover:text-gold/80 text-xs"
                      >
                        Clear all filters
                      </Button>
                    )}
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-surface-linen/30 transition-colors">
                    <td className="px-4 py-3 text-sm text-charcoal font-mono">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-charcoal/90">
                      {user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || <span className="text-charcoal-subtle italic">No name</span>}
                    </td>
                    <td className="px-4 py-3">
                      {editingUserId === user.id ? (
                        <div className="space-y-2 min-w-[280px]">
                          {editingRoles.map((role, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <select
                                value={role.roleName}
                                onChange={(e) => handleRoleChange(idx, e.target.value)}
                                className="flex-1 h-8 rounded border border-surface-border bg-surface-linen px-2 text-xs text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
                              >
                                {availableRoles.map(r => (
                                  <option key={r} value={r}>{r}</option>
                                ))}
                              </select>
                              {role.roleName === 'parish-admin' && (
                                <select
                                  value={role.parishId || ''}
                                  onChange={(e) => handleParishChange(idx, e.target.value)}
                                  className="flex-1 h-8 rounded border border-surface-border bg-surface-linen px-2 text-xs text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
                                >
                                  <option value="">Select Parish</option>
                                  {allParishes.map(parish => (
                                    <option key={parish.id} value={parish.id}>{parish.name}</option>
                                  ))}
                                </select>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveRole(idx)}
                                className="text-red-500 hover:text-red-600 h-8 w-8 p-0"
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleAddRole}
                            className="text-xs h-7 text-charcoal border-gold/50 hover:border-gold"
                          >
                            + Add Role
                          </Button>
                          {editingRoles.length === 0 && (
                            <p className="text-xs text-charcoal-muted">No roles assigned.</p>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {(user.roles || []).length === 0 ? (
                            <span className="text-xs text-charcoal-subtle italic">No roles</span>
                          ) : (
                            user.roles.map((role, idx) => {
                              const roleName = typeof role === 'string' ? role : role.name
                              const parishName = typeof role === 'object' ? role.parishName : null
                              return (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium ${
                                    roleName === 'super-admin'
                                      ? 'bg-red-100 text-red-700'
                                      : roleName === 'parish-admin'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                  title={parishName ? `Parish: ${parishName}` : undefined}
                                >
                                  {roleName}
                                  {parishName && (
                                    <span className="ml-1 opacity-70">({parishName})</span>
                                  )}
                                </span>
                              )
                            })
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {(user.parishes || []).length === 0 ? (
                          <span className="text-xs text-charcoal-subtle italic">None</span>
                        ) : (
                          user.parishes.map((parish, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 text-xs bg-gold/10 text-charcoal rounded-full"
                            >
                              {parish.name}
                              {parish.isPrimary && (
                                <span className="ml-1 text-gold font-semibold text-[10px]">PRIMARY</span>
                              )}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {editingUserId === user.id ? (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            onClick={() => handleSaveRoles(user.id)}
                            disabled={saving}
                            className="bg-gold text-black hover:bg-gold/90 h-8 text-xs"
                          >
                            {saving ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <>
                                <Save className="w-3.5 h-3.5 mr-1" />
                                Save
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancelEdit}
                            className="text-charcoal-muted hover:text-charcoal h-8 w-8 p-0"
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSendTestEmail(user.id)}
                            disabled={sendingTestEmailTo === user.id}
                            className="text-charcoal-muted hover:text-charcoal h-8 w-8 p-0"
                            title="Send test email"
                          >
                            {sendingTestEmailTo === user.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Mail className="w-3.5 h-3.5" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(user)}
                            className="text-charcoal-muted hover:text-charcoal h-8 text-xs"
                          >
                            <Edit className="w-3.5 h-3.5 mr-1" />
                            Edit
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalItems > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-surface-border bg-surface-linen/30">
            <div className="flex items-center gap-3">
              <span className="text-xs text-charcoal-muted">
                Showing {startIndex + 1}–{endIndex} of {totalItems}
              </span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-7 rounded border border-surface-border bg-white px-2 text-xs text-charcoal focus:outline-none focus:ring-1 focus:ring-gold"
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} per page</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={safePage <= 1}
                className="h-7 w-7 p-0 text-charcoal-muted hover:text-charcoal disabled:opacity-30"
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="h-7 w-7 p-0 text-charcoal-muted hover:text-charcoal disabled:opacity-30"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>

              {/* Page number buttons */}
              {(() => {
                const pages = []
                let startPage = Math.max(1, safePage - 2)
                let endPage = Math.min(totalPages, startPage + 4)
                if (endPage - startPage < 4) {
                  startPage = Math.max(1, endPage - 4)
                }
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <Button
                      key={i}
                      variant={i === safePage ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setCurrentPage(i)}
                      className={`h-7 w-7 p-0 text-xs ${
                        i === safePage
                          ? 'bg-gold text-black hover:bg-gold/90'
                          : 'text-charcoal-muted hover:text-charcoal'
                      }`}
                    >
                      {i}
                    </Button>
                  )
                }
                return pages
              })()}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="h-7 w-7 p-0 text-charcoal-muted hover:text-charcoal disabled:opacity-30"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={safePage >= totalPages}
                className="h-7 w-7 p-0 text-charcoal-muted hover:text-charcoal disabled:opacity-30"
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
